import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { TaxRate } from '../../../../../models/tax-rate';
import { AccountService } from '../../../../../services/account/account.service';
import { TaxRateService } from '../../../../../services/tax-rate/tax-rate.service';
import { CallbackResult, CallbackResultStatus } from '../../../../../utils';
import { AddTaxRateModalComponent } from './modals/add-tax-rate-modal/add-tax-rate-modal.component';
import { EditTaxRateModalComponent } from './modals/edit-tax-rate-modal/edit-tax-rate-modal.component';
import { RemoveTaxRateModalComponent } from './modals/remove-tax-rate-modal/remove-tax-rate-modal.component';
import { OrganizationConfigService } from '../../../../../services/organization-config/organization-config.service';
import { OrganizationConfig } from '../../../../../models/organization-config';
import { TaxDisplayFormat } from '../../../../../models/organization-config-enums';

@Component({
  selector: 'app-tax-rates-config',
  templateUrl: './tax-rates-config.component.html',
  styleUrls: ['./tax-rates-config.component.css']
})
export class TaxRatesConfigComponent implements OnInit {

  public taxRateList: TaxRate[];
  public orgConfig: OrganizationConfig;

  public loadingTaxRates = true;
  public loadingOrgConfig = true;
  public savingOrgConfig = false;

  public serverError: number = null;
  public errorMessage = '';

  // Make browser history available to the template.
  public history: History;

  public modalResult: CallbackResult = null;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public readonly CallbackResultStatus = CallbackResultStatus;
  public readonly TaxDisplayFormat = TaxDisplayFormat;

  constructor(
    private accountService: AccountService,
    private taxRateService: TaxRateService,
    private orgConfigService: OrganizationConfigService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().is_organization_admin) {
      this.errorMessage = 'You do not have permission to configure tax rates.';
      return;
    }

    // Make browser history available to the template.
    this.history = history;

    this.loadTaxRates();
    this.loadOrgConfig();
  }

  public taxRateResultsPresent() {
    return this.taxRateList && (this.taxRateList.length > 0);
  }

  public openAddTaxRateModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(AddTaxRateModalComponent);
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadTaxRates();
    };
  }

  public openEditTaxRateModal(taxRate: TaxRate) {
    // Create a copy of the tax rate so that the tax rate's info is only
    // updated in the table after it is saved to the API.
    const taxRateInput: TaxRate = {...taxRate};

    this.modalResult = null;

    const modalRef = this.modalService.open(EditTaxRateModalComponent);
    modalRef.componentInstance.taxRate = taxRateInput;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadTaxRates();
    };
  }

  public openRemoveTaxRateModal(taxRate: TaxRate) {
    this.modalResult = null;

    const modalRef = this.modalService.open(RemoveTaxRateModalComponent);
    modalRef.componentInstance.taxRate = taxRate;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadTaxRates();
    };
  }

  public saveOrgConfig() {
    this.savingOrgConfig = true;
    this.modalResult = null;

    this.orgConfigService
      .updateOrgConfig({
        price_includes_tax: this.orgConfig.price_includes_tax,
        tax_display_format: this.orgConfig.tax_display_format,
      })
      .then(() => {
        this.orgConfig = this.orgConfigService.getOrganizationConfig();
        this.modalResult = {
          text: 'Tax configuration updated.',
          status: CallbackResultStatus.success
        };
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.savingOrgConfig = false;});
  }

  private loadTaxRates() {
    this.loadingTaxRates = true;

    this.serverError = null;
    this.errorMessage = '';

    this.taxRateService
      .getTaxRates(1, 100)
      .then(response => { this.taxRateList = response.results; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingTaxRates = false; });
  }

  private loadOrgConfig() {
    this.loadingOrgConfig = true;

    this.orgConfigService
      .loadOrganizationConfig()
      .then(() => {
        this.orgConfig = this.orgConfigService.getOrganizationConfig();
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingOrgConfig = false; });
  }
}
